import React, { useState, useEffect, useRef } from 'react';
import { useAudio } from '../../contexts/AudioContext';

export function ProgressBar({
    width = '60%',
    trackBarHeight = '5px',
    thumbColor = '#808080',
    thumbHeight = '12px',
    thumbWidth = '12px',
    thumbBorderRadius = '50%',
    progressColor = '#808080',
    trackColor = '#F2EFF4',
    textColor = '#D9D9D9',
    fontSize = '12px',
}) {
    const { audioRef } = useAudio();
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isAudioReady, setAudioReady] = useState(false);
    const progressBarRef = useRef(null);

    useEffect(() => {
        const audio = audioRef.current;

        const setAudioData = () => {
            setDuration(audio.duration);
            setCurrentTime(audio.currentTime);
            setAudioReady(true); 
        };

        const updateProgress = () => {
            if (!isDragging) {
                setProgress((audio.currentTime / audio.duration) * 100);
                setCurrentTime(audio.currentTime);
            }
        };

        const handleAudioEnd = () => {
            setAudioReady(false);
        };

        audio.addEventListener('loadeddata', setAudioData);
        audio.addEventListener('timeupdate', updateProgress);
        audio.addEventListener('ended', handleAudioEnd); 

        return () => {
            audio.removeEventListener('loadeddata', setAudioData);
            audio.removeEventListener('timeupdate', updateProgress);
            audio.removeEventListener('ended', handleAudioEnd);
        };
    }, [audioRef, isDragging]);

    useEffect(() => {
        const handleDocumentMouseMove = (e) => {
            if (isDragging) {
                const progressBar = progressBarRef.current;
                const rect = progressBar.getBoundingClientRect();
                let offsetX = e.clientX - rect.left;
                if (offsetX < 0) {
                    offsetX = 0;
                } else if (offsetX > rect.width) {
                    offsetX = rect.width;
                }
                const newProgress = (offsetX / rect.width) * 100;
                setProgress(newProgress);
                setCurrentTime((newProgress / 100) * duration);
                audioRef.current.currentTime = (newProgress / 100) * duration;
            }
        };

        const handleDocumentMouseUp = () => {
            setIsDragging(false);
        };

        document.addEventListener('mousemove', handleDocumentMouseMove);
        document.addEventListener('mouseup', handleDocumentMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleDocumentMouseMove);
            document.removeEventListener('mouseup', handleDocumentMouseUp);
        };
    }, [audioRef, duration, isDragging]);

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    const handleProgressBarClick = (e) => {
        if (!isAudioReady) return;
        const progressBar = progressBarRef.current;
        const rect = progressBar.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const progressBarWidth = progressBar.offsetWidth;
        const newProgress = (offsetX / progressBarWidth) * 100;
        setProgress(newProgress);
        setCurrentTime((newProgress / 100) * duration);
        audioRef.current.currentTime = (newProgress / 100) * duration;
    };

    const handleThumbMouseDown = () => {
        setIsDragging(true);
    };

    const handleThumbMouseMove = (e) => {
        if (isDragging && ! isAudioReady) {
            const progressBar = progressBarRef.current;
            const rect = progressBar.getBoundingClientRect();
            let offsetX = e.clientX - rect.left;
            if (offsetX < 0) {
                offsetX = 0;
            } else if (offsetX > rect.width) {
                offsetX = rect.width;
            }
            const newProgress = (offsetX / rect.width) * 100;
            setProgress(newProgress);
            setCurrentTime((newProgress / 100) * duration);
            audioRef.current.currentTime = (newProgress / 100) * duration;
        }
    };

    const handleThumbMouseUp = () => {
        setIsDragging(false);
    };

    return (
        <div style={{ width, display: 'flex', alignItems: 'center' }}>
            <div style={{ flexGrow: 1, marginRight: '10px', position: 'relative' }} ref={progressBarRef} onClick={handleProgressBarClick} onMouseMove={handleThumbMouseMove} onMouseDown={handleThumbMouseDown} onMouseUp={handleThumbMouseUp}>
                <div style={{ height: trackBarHeight, backgroundColor: trackColor, borderRadius: trackBarHeight, cursor: 'pointer' }}>
                    <div
                        style={{
                            height: '100%',
                            width: `${progress}%`,
                            backgroundColor: progressColor,
                            borderRadius: trackBarHeight,
                            position: 'relative',
                        }}
                    >
                    </div>
                </div>
                <span
                    style={{
                        position: 'absolute',
                        top: `-${parseInt(thumbHeight, 10) + 8}px`, // Adjusted to display above the thumb
                        left: `${progress}%`, // Center text above thumb
                        transform: 'translateX(-50%)',
                        fontSize: fontSize,
                        color: textColor,
                        userSelect: 'none'
                    }}
                >
                    {formatTime(currentTime)}
                </span>
                <div
                    style={{
                        position: 'absolute',
                        top: `calc(50% - ${thumbHeight} / 2)`,
                        left: `calc(${progress}% - (${thumbWidth} / 2))`, // Corrected to move with progress
                        // transform: 'translateX(-50%)', // Ensure it's centered at the start of the progress bar
                        height: thumbHeight,
                        width: thumbWidth,
                        backgroundColor: thumbColor,
                        borderRadius: thumbBorderRadius,
                        cursor: 'pointer',
                    }}
                    
                ></div>
            </div>
            <div style={{ fontSize: fontSize, color: textColor, userSelect: 'none' }}>{formatTime(duration)}</div>
        </div>
    );
};
